import { Entity } from './entity';
import {
  CoursePaidBy,
  CourseStatus,
  ICourse,
  ICourseStorage,
  UserCourseStatus,
  ISourceTemplate,
  ITextBook,
} from '@core/interfaces';
import { User } from './user.entity';

export class Course extends Entity<ICourse> {
  constructor(props: ICourse) {
    super(props);
  }

  get id() {
    return this.props.id;
  }

  get icon() {
    return this.props.icon;
  }

  get name() {
    return this.props.name;
  }

  get courseCode() {
    return this.props.courseCode;
  }

  get description() {
    return this.props.description;
  }

  get userCount() {
    return this.props.userCount;
  }

  get instructors() {
    return this.props.instructors && this.props.instructors.map(instructor => new User(instructor));
  }

  get instructorNames(): string {
    if (!this.instructors || !this.instructors.length) {
      return '';
    }

    return this.instructors.map(instructor => instructor.name).join(', ');
  }

  get instructorEmails(): string {
    if (!this.instructors || !this.instructors.length) {
      return '';
    }

    return this.instructors.map(instructor => instructor.email).join(', ');
  }

  get simulation() {
    return this.props.simulation;
  }

  get simulationName() {
    return this.props.simulation?.name ?? '';
  }

  get status(): CourseStatus {
    return this.props.status;
  }

  get statusName(): string {
    return CourseStatus?.[this.props.status] ?? '';
  }

  get isActive(): boolean {
    return this.props.status === CourseStatus.Active;
  }

  get isInactive(): boolean {
    return this.props.status === CourseStatus.Inactive;
  }

  get isArchived(): boolean {
    return this.props.status === CourseStatus.Archived;
  }

  get statusClass(): string {
    return this.isActive ? 'active' : 'inactive';
  }

  get statusBadgeClass(): string {
    return this.isActive ? 'badge-success' : this.isInactive ? 'badge-warning' : 'badge-secondary';
  }

  get usercourse(): { status: UserCourseStatus; paid: boolean; logins: number } {
    return this.props.usercourse;
  }

  get userCourseStatus(): UserCourseStatus {
    return this.usercourse.status;
  }

  get isPaid(): boolean {
    return this.usercourse.paid;
  }

  get logins(): number {
    return this.usercourse.logins;
  }

  get isPaidName(): string {
    return this.usercourse.paid ? 'Paid' : 'Unpaid';
  }

  get userCourseStatusName(): string {
    return [UserCourseStatus.Active, UserCourseStatus.Inactive].includes(this.userCourseStatus)
      ? UserCourseStatus[this.userCourseStatus]
      : 'WAITING_FOR_APPROVAL';
  }

  get userCourseStatusClass(): string {
    return [UserCourseStatus.Active, UserCourseStatus.Inactive].includes(this.userCourseStatus)
      ? UserCourseStatus[this.userCourseStatus].toLowerCase()
      : 'pending';
  }

  get userCourseStatusBadgeClass(): string {
    return this.userCourseStatus === UserCourseStatus.Active
      ? 'badge-success'
      : this.userCourseStatus === UserCourseStatus.Inactive
        ? 'badge-warning'
        : 'badge-secondary';
  }

  get paidBy(): CoursePaidBy {
    return this.props.paidBy;
  }

  get paidByName(): string {
    return CoursePaidBy?.[this.props.paidBy] ?? '';
  }

  get isPaidByInstitute(): boolean {
    return this.props.paidBy === CoursePaidBy.Institute;
  }

  get isPaidByPlayer(): boolean {
    return this.props.paidBy === CoursePaidBy.Player;
  }

  get paidByIcon(): string {
    return this.props.paidBy === CoursePaidBy.Institute
      ? 'account_balance'
      : this.props.paidBy === CoursePaidBy.Player
        ? 'person'
        : '';
  }

  get price() {
    return this.props.price;
  }

  get autoApproveUser() {
    return this.props.autoApproveUser;
  }

  get locale() {
    return this.props.locale;
  }

  get endDate() {
    return this.props.endDate;
  }

  getLocaleEndDate(locale: string | string[]) {
    return (
      this.props.endDate &&
      new Date(this.props.endDate).toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  }

  get created() {
    return this.props.created;
  }

  getLocaleCreated(locale: string | string[]) {
    return (
      this.props.created &&
      new Date(this.props.created).toLocaleString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  }

  get includeTextbook() {
    return this.props.includeTextbook;
  }

  get textbook() {
    return this.props.textbook;
  }

  get textbookName() {
    return this.props.textbook?.entryUrl ?? '';
  }

  get storage() {
    return this.props.storage;
  }

  set storage(value: ICourseStorage) {
    this.props.storage = value;
  }

  get sourceTemplate() {
    return this.props.gameTemplate && new SourceTemplate(this.props.gameTemplate);
  }
}

export class SourceTemplate extends Entity<ISourceTemplate> {
  constructor(props: ISourceTemplate) {
    super(props);
  }

  get id() {
    return this.props.id;
  }

  get name() {
    return this.props.name;
  }
}

export class TextBook extends Entity<ITextBook> {
  constructor(props: ITextBook) {
    super(props);
  }

  get id() {
    return this.props.id;
  }

  get title() {
    return this.props.title;
  }

  get edition() {
    return this.props.edition;
  }

  get isbn() {
    return this.props.isbn;
  }

  get entryUrl() {
    return this.props.entryUrl;
  }

  get url() {
    return this.props.entryUrl;
  }

  get publishedDate() {
    return this.props.publishedDate;
  }

  get displayName() {
    return this.edition ? `${this.title} (${this.edition})` : this.title;
  }
}
